import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, HardHat, Home, Loader2 } from 'lucide-react';
import { sitesService } from '../../services/sitesService';
import type { ProjectType } from '../../types/api';

const CHOICES: { value: ProjectType; label: string; icon: typeof Home }[] = [
  { value: 'residential', label: 'Residential', icon: Home },
  { value: 'development', label: 'Development', icon: HardHat },
  { value: 'commercial', label: 'Commercial', icon: Building2 },
];

/**
 * Robert Keller 2026-10-19: "This site wont load." The inspection form cannot
 * pick its questions without the site's project type, and the old message sent
 * the inspector to the site detail page, which the phone does not reach. The
 * inspector sets the type here instead and the form loads.
 */
export function ProjectTypePicker({
  siteId,
  siteName,
  onSet,
}: {
  siteId: string;
  siteName?: string;
  onSet: () => void;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (project_type: ProjectType) =>
      sitesService.updateSiteDetails(siteId, { project_type }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-details', siteId] });
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      onSet();
    },
  });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
      <div className="p-8 max-w-md w-full">
        <p className="text-lg font-semibold text-slate-900 dark:text-white mb-1 text-center">
          What kind of site is this?
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 text-center">
          {siteName ? `${siteName} has ` : 'This site has '}no project type yet.
          Pick one and the inspection form will load.
        </p>
        <div className="space-y-3">
          {CHOICES.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => mutation.mutate(value)}
              disabled={mutation.isPending}
              className="w-full flex items-center gap-3 px-4 py-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-left text-base font-medium text-slate-900 dark:text-white hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 disabled:opacity-50"
            >
              <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400 shrink-0" />
              {label}
              {mutation.isPending && mutation.variables === value && (
                <Loader2 className="w-4 h-4 animate-spin ml-auto" />
              )}
            </button>
          ))}
        </div>
        {mutation.isError && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400 text-center">
            Couldn't save the type. Try again, or ask the office to set it.
          </p>
        )}
      </div>
    </div>
  );
}
